import React, { useMemo } from "react";

const BlueRose = ({ size }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} style={{ display: "block" }}>
    <defs>
      <radialGradient id={`br-${size}`} cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="#BAE6FD" />
        <stop offset="45%" stopColor="#60A5FA" />
        <stop offset="80%" stopColor="#2563EB" />
        <stop offset="100%" stopColor="#1E3A8A" />
      </radialGradient>
    </defs>
    <g fill={`url(#br-${size})`}>
      {/* outer petals */}
      <ellipse cx="16" cy="10" rx="6" ry="7" />
      <ellipse cx="9" cy="14" rx="6" ry="7" transform="rotate(-50 9 14)" />
      <ellipse cx="23" cy="14" rx="6" ry="7" transform="rotate(50 23 14)" />
      <ellipse cx="11" cy="22" rx="6" ry="7" transform="rotate(-110 11 22)" />
      <ellipse cx="21" cy="22" rx="6" ry="7" transform="rotate(110 21 22)" />
      {/* center bud */}
      <circle cx="16" cy="16" r="4.5" fill="#1E3A8A" />
      <circle cx="16" cy="16" r="2" fill="#60A5FA" />
    </g>
  </svg>
);

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

/**
 * Bursts blue rose petals at multiple origin points.
 * Each burst spawns N petals that explode outward + gently fall.
 */
export default function RoseBurst({ active }) {
  const bursts = useMemo(
    () => [
      { x: "50%", y: "50%", count: 50, delay: 0 },
      { x: "20%", y: "30%", count: 22, delay: 0.25 },
      { x: "80%", y: "30%", count: 22, delay: 0.3 },
      { x: "25%", y: "75%", count: 22, delay: 0.45 },
      { x: "75%", y: "75%", count: 22, delay: 0.5 },
    ],
    []
  );

  const particles = useMemo(() => {
    const list = [];
    bursts.forEach((b) => {
      for (let i = 0; i < b.count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 180 + Math.random() * 420;
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance;
        list.push({
          id: uid(),
          x: b.x,
          y: b.y,
          dx,
          dy,
          delay: b.delay + Math.random() * 0.15,
          size: 18 + Math.random() * 32,
          rotStart: Math.random() * 360,
          rotEnd: Math.random() * 720 - 360,
          fallY: 200 + Math.random() * 300,
          duration: 2.4 + Math.random() * 1.6,
        });
      }
    });
    return list;
  }, [bursts]);

  if (!active) return null;

  return (
    <div data-testid="rose-burst" aria-hidden className="rose-burst-layer">
      {/* radial flash */}
      <div className="rose-burst-flash" />
      {particles.map((p) => (
        <span
          key={p.id}
          className="rose-particle"
          style={{
            left: p.x,
            top: p.y,
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
            "--fy": `${p.fallY}px`,
            "--r0": `${p.rotStart}deg`,
            "--r1": `${p.rotEnd}deg`,
            "--delay": `${p.delay}s`,
            "--dur": `${p.duration}s`,
          }}
        >
          <BlueRose size={p.size} />
        </span>
      ))}
    </div>
  );
}
